import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
import { DollarSign, Plus, Trash2, Save, RefreshCw, Settings, Pencil, Palette } from 'lucide-react';

const PricingTab = ({ token }) => {
  const [activeProduct, setActiveProduct] = useState('roller-shades');
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedPricing, setEditedPricing] = useState({});

  const productTypes = [
    { id: 'roller-shades', name: 'Roller Shades', icon: '🎚️' },
    { id: 'motorized-drapes', name: 'Motorized Drapes', icon: '🪟' },
    { id: 'roman-shades', name: 'Roman Shades', icon: '📜' },
  ];

  useEffect(() => {
    fetchPricing();
  }, [activeProduct]);

  const fetchPricing = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/pricing/${activeProduct}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(response.data);
      setEditedPricing(JSON.parse(JSON.stringify(response.data)));
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialChange = (materialId, field, value) => {
    setEditedPricing(prev => ({
      ...prev,
      materials: prev.materials.map(m => 
        m.id === materialId ? { ...m, [field]: parseFloat(value) || 0 } : m
      )
    }));
  };

  const handleOptionChange = (optionType, optionId, value) => {
    setEditedPricing(prev => {
      const options = [...(prev[optionType] || [])];
      const idx = options.findIndex(o => o.id === optionId);
      if (idx !== -1) {
        // Handle both "price" and "price_modifier" fields
        if ('price_modifier' in options[idx]) {
          options[idx] = { ...options[idx], price_modifier: parseFloat(value) || 0 };
        } else {
          options[idx] = { ...options[idx], price: parseFloat(value) || 0 };
        }
      }
      return { ...prev, [optionType]: options };
    });
  };

  const savePricing = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/pricing/${activeProduct}`, editedPricing, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pricing saved successfully!');
      setPricing(JSON.parse(JSON.stringify(editedPricing)));
    } catch (error) {
      toast.error('Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  const resetPricing = async () => {
    if (!confirm('Reset pricing to defaults? This cannot be undone.')) return;
    try {
      await axios.post(`${API_URL}/admin/pricing/reset/${activeProduct}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pricing reset to defaults');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to reset pricing');
    }
  };

  const hasChanges = JSON.stringify(pricing) !== JSON.stringify(editedPricing);

  const formatPrice = (price) => `$${(price || 0).toFixed(2)}`;

  const getOptionPrice = (option) => {
    return option.price_modifier !== undefined ? option.price_modifier : (option.price || 0);
  };

  return (
    <div data-testid="pricing-tab">
      {/* Product Selector */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Pricing Management</h2>
          <p className="text-neutral-500 text-sm">Configure pricing for each product type</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={resetPricing}
            className="text-neutral-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Reset to Defaults
          </Button>
          <Button
            onClick={savePricing}
            disabled={!hasChanges || saving}
            className="btn-primary"
            data-testid="save-pricing-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Product Type Tabs */}
      <div className="flex gap-2 mb-6">
        {productTypes.map((product) => (
          <button
            key={product.id}
            onClick={() => setActiveProduct(product.id)}
            className={`px-4 py-2 rounded-lg border transition-all ${
              activeProduct === product.id
                ? 'border-neutral-900 bg-neutral-900 text-white'
                : 'border-neutral-200 hover:border-neutral-400'
            }`}
            data-testid={`product-tab-${product.id}`}
          >
            <span className="mr-2">{product.icon}</span>
            {product.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Materials Pricing */}
          {editedPricing?.materials && (
            <div>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Palette className="w-4 h-4" /> Fabric Materials
              </h3>
              <div className="bg-neutral-50 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="text-left p-3 text-xs font-medium text-neutral-500 uppercase">Material</th>
                      <th className="text-right p-3 text-xs font-medium text-neutral-500 uppercase">Base Price</th>
                      <th className="text-right p-3 text-xs font-medium text-neutral-500 uppercase">Per Sq Ft</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editedPricing.materials.map((material) => (
                      <tr key={material.id} className="border-b border-neutral-100 last:border-0">
                        <td className="p-3">
                          <p className="font-medium text-sm">{material.name}</p>
                          <p className="text-xs text-neutral-500">{material.description}</p>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-neutral-400">$</span>
                            <Input
                              type="number"
                              value={material.base_price}
                              onChange={(e) => handleMaterialChange(material.id, 'base_price', e.target.value)}
                              className="w-24 text-right h-8"
                              step="0.01"
                              data-testid={`material-base-${material.id}`}
                            />
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-neutral-400">$</span>
                            <Input
                              type="number"
                              value={material.price_per_sqft}
                              onChange={(e) => handleMaterialChange(material.id, 'price_per_sqft', e.target.value)}
                              className="w-24 text-right h-8"
                              step="0.01"
                              data-testid={`material-sqft-${material.id}`}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Mount Types */}
          {editedPricing?.mount_types && (
            <div>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">Mount Types</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {editedPricing.mount_types.map((mount) => (
                  <div key={mount.id} className="bg-neutral-50 p-4 rounded-lg">
                    <p className="font-medium text-sm mb-1">{mount.name}</p>
                    <p className="text-xs text-neutral-500 mb-3">{mount.description}</p>
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-400 text-sm">+$</span>
                      <Input
                        type="number"
                        value={getOptionPrice(mount)}
                        onChange={(e) => handleOptionChange('mount_types', mount.id, e.target.value)}
                        className="w-20 text-right h-8"
                        step="0.01"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Control Types */}
          {editedPricing?.control_types && (
            <div>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">Control Types</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {editedPricing.control_types.map((control) => (
                  <div key={control.id} className="bg-neutral-50 p-4 rounded-lg">
                    <p className="font-medium text-sm mb-1">{control.name}</p>
                    <p className="text-xs text-neutral-500 mb-3">{control.description}</p>
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-400 text-sm">+$</span>
                      <Input
                        type="number"
                        value={control.price || 0}
                        onChange={(e) => handleOptionChange('control_types', control.id, e.target.value)}
                        className="w-20 text-right h-8"
                        step="0.01"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product-specific options */}
          {/* Drape Pleat Styles */}
          {editedPricing?.pleat_styles && (
            <div>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">Pleat Styles</h3>
              <div className="grid sm:grid-cols-4 gap-3">
                {editedPricing.pleat_styles.map((pleat) => (
                  <div key={pleat.id} className="bg-neutral-50 p-4 rounded-lg">
                    <p className="font-medium text-sm mb-1">{pleat.name}</p>
                    <p className="text-xs text-neutral-500 mb-3">{pleat.description}</p>
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-400 text-sm">+$</span>
                      <Input
                        type="number"
                        value={pleat.price || 0}
                        onChange={(e) => handleOptionChange('pleat_styles', pleat.id, e.target.value)}
                        className="w-20 text-right h-8"
                        step="0.01"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Drape Track Types */}
          {editedPricing?.track_types && (
            <div>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">Track Types</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {editedPricing.track_types.map((track) => (
                  <div key={track.id} className="bg-neutral-50 p-4 rounded-lg">
                    <p className="font-medium text-sm mb-1">{track.name}</p>
                    <p className="text-xs text-neutral-500 mb-3">{track.description}</p>
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-400 text-sm">+$</span>
                      <Input
                        type="number"
                        value={track.price || 0}
                        onChange={(e) => handleOptionChange('track_types', track.id, e.target.value)}
                        className="w-20 text-right h-8"
                        step="0.01"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Roman Fold Styles + Linings */}
          {[['fold_styles', 'Fold Styles'], ['linings', 'Roman Linings']].filter(([k]) => editedPricing?.[k]).map(([key, title]) => (
            <div key={key} data-testid={`pricing-${key}`}>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">{title}</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {editedPricing[key].map((fold) => (
                  <div key={fold.id} className="bg-neutral-50 p-4 rounded-lg">
                    <p className="font-medium text-sm mb-1">{fold.name}</p>
                    <p className="text-xs text-neutral-500 mb-3">{fold.description}</p>
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-400 text-sm">+$</span>
                      <Input
                        type="number"
                        value={fold.price || 0}
                        onChange={(e) => handleOptionChange(key, fold.id, e.target.value)}
                        className="w-20 text-right h-8"
                        step="0.01"
                        data-testid={`pricing-${key}-${fold.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Bracket Colors (Roller Shades) */}
          {editedPricing?.bracket_colors && (
            <div>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">Bracket Colors</h3>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {editedPricing.bracket_colors.map((bracket) => (
                  <div key={bracket.id} className="bg-neutral-50 p-3 rounded-lg text-center">
                    <div 
                      className="w-8 h-8 rounded-full mx-auto mb-2 border border-neutral-200"
                      style={{ backgroundColor: bracket.hex }}
                    />
                    <p className="font-medium text-xs mb-2">{bracket.name}</p>
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-neutral-400 text-xs">+$</span>
                      <Input
                        type="number"
                        value={bracket.price || 0}
                        onChange={(e) => handleOptionChange('bracket_colors', bracket.id, e.target.value)}
                        className="w-16 text-right h-7 text-xs"
                        step="0.01"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bottom Bar Styles (Roller Shades) */}
          {editedPricing?.bottom_bar_styles && (
            <div>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">Bottom Bar Styles</h3>
              <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {editedPricing.bottom_bar_styles.map((bar) => (
                  <div key={bar.id} className="bg-neutral-50 p-4 rounded-lg">
                    <p className="font-medium text-sm mb-1">{bar.name}</p>
                    <p className="text-xs text-neutral-500 mb-3">{bar.description}</p>
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-400 text-sm">+$</span>
                      <Input
                        type="number"
                        value={bar.price || 0}
                        onChange={(e) => handleOptionChange('bottom_bar_styles', bar.id, e.target.value)}
                        className="w-20 text-right h-8"
                        step="0.01"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optional Features */}
          {editedPricing?.optional_features && (
            <div>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">Optional Features</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {editedPricing.optional_features.map((feature) => (
                  <div key={feature.id} className="bg-neutral-50 p-4 rounded-lg">
                    <p className="font-medium text-sm mb-1">{feature.name}</p>
                    <p className="text-xs text-neutral-500 mb-3 line-clamp-2">{feature.description}</p>
                    <div className="flex items-center gap-1">
                      <span className="text-neutral-400 text-sm">+$</span>
                      <Input
                        type="number"
                        value={feature.price || 0}
                        onChange={(e) => handleOptionChange('optional_features', feature.id, e.target.value)}
                        className="w-20 text-right h-8"
                        step="0.01"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Change indicator */}
      {hasChanges && (
        <div className="fixed bottom-4 right-4 bg-amber-100 border border-amber-300 px-4 py-2 rounded-lg shadow-lg text-sm">
          <span className="text-amber-800">You have unsaved changes</span>
        </div>
      )}
    </div>
  );
};

// Pages & SEO Management Tab


export default PricingTab;
